import Vue from "vue";
import providers from "@/lib/providers";
import { computed, ref } from "@vue/composition-api";
import { Subscription, SubscriptionsSearchResult } from "@teacherskaya/api-provider/dist/api/SubscriptionProvider";
import { FragmentFields } from "@teacherskaya/api-provider/dist/api/types";

type TResolveFunc = (
  userId: string,
  page: number,
  size: number,
  fields?: FragmentFields<Subscription>
) => Promise<SubscriptionsSearchResult>;

export default function useRecurring() {
  const response = ref<SubscriptionsSearchResult | null>(null);
  const loading = ref(false);
  const subscriptions = computed(() => response.value?.result ?? []);

  const onCancel = async (subscription: Subscription, removeAccess: boolean = false) => {
    if (!response.value) return;
    let responseCancel: boolean | null = null;

    if (subscription.recurringBillingSchedule) {
      responseCancel = await providers.RecurringBillingScheduleProvider.cancel({
        data: { id: subscription.recurringBillingSchedule.id, removeAccess },
      });
    } else if (removeAccess) {
      responseCancel = await providers.SubscriptionProvider.cancel({ subscriptionId: subscription.id });
    }
    if (responseCancel === null) return;
    Vue.set(
      response.value,
      "result",
      response.value.result.map(i => {
        if (i.recurringBillingScheduleId === subscription.recurringBillingScheduleId && i.recurringBillingSchedule) {
          i.recurringBillingSchedule.cancelled = responseCancel!;
        }
        if (i.id === subscription.id && removeAccess && responseCancel) {
          i.expirationTime = new Date(Date.now() - 1000).toISOString();
        }
        return i;
      })
    );
  };

  const DEFAULT_FIELDS: FragmentFields<Subscription> = [
    "id",
    "amount",
    "consumerId",
    "providerId",
    "expirationTime",
    "recurringBillingScheduleId",
    { consumer: ["screenName"] },
    { provider: ["screenName"] },
    {
      recurringBillingSchedule: ["id", "cancelled", { cost: ["cost", "currency"] }, { schedule: ["nextBillingTime"] }],
    },
  ];

  const getRecurringByProvider = (
    userId: string,
    page: number,
    size: number,
    fields?: FragmentFields<Subscription>
  ) => {
    return providers.SubscriptionProvider.search(
      {
        data: { providerId: userId, sort: "CREATE_TIME,DESC", size, page },
      },
      fields
    );
  };

  const getRecurrings = (userId: string, page: number, size: number, fields?: FragmentFields<Subscription>) => {
    return providers.SubscriptionProvider.search(
      {
        data: { consumerId: userId, sort: "CREATE_TIME,DESC", size, page },
      },
      fields
    );
  };

  const getRecurringData = async (
    resolveFunc: TResolveFunc,
    userId: string,
    page: number = 0,
    size: number = 20,
    fields: FragmentFields<Subscription> = DEFAULT_FIELDS
  ) => {
    if (loading.value) return;
    try {
      loading.value = true;
      response.value = await resolveFunc(userId, page, size, fields);
    } finally {
      loading.value = false;
    }
  };

  return {
    response,
    loading,
    subscriptions,
    onCancel,
    getRecurringByProvider,
    getRecurrings,
    getRecurringData,
  };
}
